import { useCallback, useMemo, useState } from "react";

import TextField from "@material-ui/core/TextField";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import ListSubheader from "@material-ui/core/ListSubheader";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import IconButton from "@material-ui/core/IconButton";

import DeleteForeverIcon from "@material-ui/icons/DeleteForever";
import EditIcon from "@material-ui/icons/Edit";
import SaveIcon from "@material-ui/icons/Save";
import TextFormatIcon from "@material-ui/icons/TextFormat";
import EuroIcon from "@material-ui/icons/Euro";
import NotesIcon from "@material-ui/icons/Notes";
import CategoryIcon from "@material-ui/icons/Category";
import AvTimerIcon from "@material-ui/icons/AvTimer";
import SortIcon from "@material-ui/icons/Sort";
import ShoppingCartIcon from "@material-ui/icons/ShoppingCart";

import { Apartment, ServiceTypeListItem } from "../../../graphql/models";

import useServiceListItemEditorStyle from "./service_list_item_editor.style";
import { MUTATIONS } from "../../../graphql";
import { useSnackbarMessage, useSnackbarMutation } from "../../../hooks";
import { Checkbox, MenuItem, Select } from "@material-ui/core";
import { ConfirmButtonWithDialog, ImageEntry, ImagePicker, ImagePickerType } from "..";
import { i18nForFields } from "../../../i18n";
import { I18NTextField } from "../i18n_text_field";

export interface ServiceListItemEditorProps {
  isComposable: boolean;
  askCategories?: boolean;
  askRequiredTime?: boolean;
  serviceListItem: ServiceTypeListItem;
  targetApartment?: Apartment;
  suggestedCategories: {
    it: string[];
    en: string[];
  };
  onUpdated: (item: ServiceTypeListItem) => void;
  onDeleted: () => void;
  readonly?: boolean;
}

export function ServiceListItemEditor(props: ServiceListItemEditorProps) {
  const {
    isComposable,
    askCategories = false,
    askRequiredTime = false,
    serviceListItem,
    targetApartment,
    suggestedCategories,
    onUpdated,
    onDeleted,
    readonly = false,
  } = props;

  const { raiseSuccessSnackbar } = useSnackbarMessage();

  const mutation = Boolean(serviceListItem?.id)
    ? MUTATIONS.UPDATE_SERVICE_TYPE_LIST_ITEM
    : MUTATIONS.CREATE_SERVICE_TYPE_LIST_ITEM;

  const classes = useServiceListItemEditorStyle();

  const [isReadOnly, setIsReadOnly] = useState(Boolean(serviceListItem?.id));

  // const [label, setLabel] = useState<string>(serviceListItem.label ?? "");
  const [imageUrl, setImageUrl] = useState<string | undefined>(serviceListItem?.imageUrl);
  const [price, setPrice] = useState<number>(serviceListItem.price ?? 0);
  const [promoPrice, setPromoPrice] = useState<number>(
    serviceListItem.promoPrice ?? 0
  );
  const [inPromo, setInPromo] = useState(serviceListItem.inPromo ?? false);
  const [categories, setCategories] = useState<string[]>(
    serviceListItem.categories ?? []
  );
  // const [notes, setNotes] = useState<string | undefined>(serviceListItem.notes);
  const [requiredTime, setRequiredTime] = useState<number | undefined>(
    serviceListItem.requiredTime
  );
  const [i18nFields, setI18nFields] = useState<
    Partial<
      i18nForFields<"categories", string[]> &
        i18nForFields<"notes" | "label" | "description" | "prefix" | "suffix">
    >
  >(serviceListItem?.i18n ?? {});
  const [minSQ, setMinSQ] = useState(serviceListItem?.minSellableQuantity);
  const [maxSQ, setMaxSQ] = useState(serviceListItem?.maxSellableQuantity);

  const triggerSetCategories = (itCategories: string[]) => {
    const selectedIndexes = itCategories.map((v) =>
      suggestedCategories.it.findIndex((e) => e === v)
    );

    const i18nCopy = { ...i18nFields };
    i18nCopy["categories_it"] = itCategories;
    i18nCopy["categories_en"] = selectedIndexes.map(
      (index) => suggestedCategories.en[index]
    );

    setI18nFields({ ...i18nCopy });
    setCategories(itCategories);
  };

  const [invokeCreateOrUpdate] = useSnackbarMutation(mutation, {
    onCompleted: () => {
      const updatedObj = {
        ...serviceListItem,
        // label,
        price,
        inPromo,
        promoPrice,
        categories,
        // notes,
        requiredTime,
        i18n: i18nFields,
        targetApartment: targetApartment
          ? {
              building: {
                code: targetApartment.building.name, // TODO fix this
                id: targetApartment.building.id,
              },
              code: targetApartment.code,
              id: targetApartment.id,
            }
          : void 0,
      };
      if (isComposable && minSQ) updatedObj.minSellableQuantity = minSQ;
      if (isComposable && maxSQ) updatedObj.maxSellableQuantity = maxSQ;
      onUpdated(updatedObj);
      setIsReadOnly(true);
      raiseSuccessSnackbar("Elemento aggiornato correttamente");
    },
  });

  const [invokeDelete] = useSnackbarMutation(
    MUTATIONS.DELETE_SERVICE_TYPE_LIST_ITEM,
    {
      onCompleted: () => {
        raiseSuccessSnackbar("Elemento eliminato correttamente");
        onDeleted();
      },
    }
  );

  const displayDeleteButton = useMemo(() => {
    return Boolean(serviceListItem?.id);
  }, [serviceListItem]);

  const toggleEditMode = () => {
    if (!isReadOnly) {
      commitChanges();
    } else {
      setIsReadOnly(false);
    }
  };

  const commitChanges = () => {
    invokeCreateOrUpdate({
      variables: {
        id: serviceListItem?.id,
        serviceTypeListItemData: {
          // label,
          imageUrl,
          price,
          inPromo,
          promoPrice,
          categories,
          // notes,
          requiredTime,
          service: serviceListItem.service,
          type: serviceListItem.type,
          order: serviceListItem.order,
          i18n: i18nFields,
          targetApartment: targetApartment
            ? {
                building: {
                  code: targetApartment.building.name, // TODO fix this
                  id: targetApartment.building.id,
                },
                code: targetApartment.code,
                id: targetApartment.id,
              }
            : void 0,
          minSellableQuantity: minSQ,
          maxSellableQuantity: maxSQ,
        } as Partial<ServiceTypeListItem>,
      },
    });
  };

  const onFormSubmit = (evt) => {
    evt.preventDefault();
    commitChanges();
  };

  const triggerDelete = () => {
    invokeDelete({
      variables: {
        id: serviceListItem.id,
      },
    });
  };

  const handleSetMinSQ = useCallback((inputValue: string) => {
    if (inputValue?.trim() === "") setMinSQ(undefined);
    const n = Number.parseFloat(inputValue);
    if (isNaN(n)) setMinSQ(undefined);
    setMinSQ(n);
  }, []);
  const handleSetMaxSQ = useCallback((inputValue: string) => {
    if (inputValue?.trim() === "") setMaxSQ(undefined);
    const n = Number.parseFloat(inputValue);
    if (isNaN(n)) setMaxSQ(undefined);
    setMaxSQ(n);
  }, []);

  return (
    <Card variant="outlined">
      <CardContent>
        <form noValidate autoComplete="off" onSubmit={onFormSubmit}>
          <List
            component="nav"
            aria-labelledby={`service-list-item-${serviceListItem?.id}-data-list-subheader`}
            subheader={
              <>
                <ListSubheader
                  component="div"
                  id={`service-list-item-${serviceListItem?.id}-data-subheader`}
                >
                  Proprietà elemento di listino
                  {!readonly && (
                    <ListItemSecondaryAction>
                      <IconButton
                        onClick={toggleEditMode}
                        edge="end"
                        aria-label="edit"
                      >
                        {isReadOnly ? <EditIcon /> : <SaveIcon />}
                      </IconButton>
                      {displayDeleteButton && (
                        <ConfirmButtonWithDialog
                          startIcon={<DeleteForeverIcon />}
                          dialogContent="Confermi di voler eliminare questo elemento?"
                          onConfirm={triggerDelete}
                          onUndo={() => void 0}
                          dialogProps={{
                            fullScreen: false,
                            title: "Conferma eliminazione elemento listino",
                          }}
                          Component={IconButton}
                        >
                          <DeleteForeverIcon />
                        </ConfirmButtonWithDialog>
                        // <IconButton onClick={triggerDelete} edge="end" aria-label="edit">
                        //   <DeleteForeverIcon />
                        // </IconButton>
                      )}
                    </ListItemSecondaryAction>
                  )}
                </ListSubheader>
              </>
            }
            style={{ width: "100%" }}
          >
            <>
              <ImagePicker
                disabled={isReadOnly || readonly}
                height={100}
                defaultValue={imageUrl}
                onUploaded={(entry) => setImageUrl(entry.url)}
              />
              <ListItem>
                <ListItemIcon>
                  <TextFormatIcon />
                </ListItemIcon>
                <I18NTextField
                  className={classes.textField}
                  variant="outlined"
                  value={i18nFields}
                  onChange={setI18nFields}
                  disabled={isReadOnly || readonly}
                  label="Nome"
                  i18nFieldName="label"
                />
              </ListItem>
              {isComposable && (
                <ListItem>
                  <ListItemIcon>
                    <TextFormatIcon />
                  </ListItemIcon>
                  <I18NTextField
                    className={classes.textField}
                    variant="outlined"
                    value={i18nFields}
                    onChange={setI18nFields}
                    disabled={isReadOnly || readonly}
                    label="Prefisso per prezzo"
                    i18nFieldName="prefix"
                  />
                </ListItem>
              )}
              <ListItem>
                <ListItemIcon>
                  <EuroIcon />
                </ListItemIcon>
                <TextField
                  className={classes.textField}
                  type="number"
                  variant="outlined"
                  defaultValue={price}
                  onChange={(evt) => setPrice(Number(evt.target.value))}
                  disabled={isReadOnly || readonly}
                  label="Importo"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <EuroIcon />
                </ListItemIcon>
                <Checkbox
                  readOnly={isReadOnly || readonly}
                  checked={inPromo}
                  onChange={(e, checked) => setInPromo(checked)}
                />{" "}
                In Promo!
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <EuroIcon />
                </ListItemIcon>
                <TextField
                  className={classes.textField}
                  type="number"
                  variant="outlined"
                  defaultValue={promoPrice}
                  onChange={(evt) => setPromoPrice(Number(evt.target.value))}
                  disabled={isReadOnly || readonly}
                  label="Importo PROMO"
                />
              </ListItem>
              {isComposable && (
                <ListItem>
                  <ListItemIcon>
                    <TextFormatIcon />
                  </ListItemIcon>
                  <I18NTextField
                    className={classes.textField}
                    variant="outlined"
                    value={i18nFields}
                    onChange={setI18nFields}
                    disabled={isReadOnly || readonly}
                    label="Suffisso per prezzo"
                    i18nFieldName="suffix"
                  />
                </ListItem>
              )}
              <ListItem>
                <ListItemIcon>
                  <NotesIcon />
                </ListItemIcon>
                <I18NTextField
                  className={classes.textField}
                  variant="outlined"
                  value={i18nFields}
                  onChange={setI18nFields}
                  disabled={isReadOnly || readonly}
                  label="Note"
                  i18nFieldName="notes"
                />
              </ListItem>
              {askCategories && (
                <ListItem>
                  <ListItemIcon>
                    <CategoryIcon />
                  </ListItemIcon>
                  <Select
                    multiple
                    label="Categorie"
                    variant="outlined"
                    className={classes.textField}
                    disabled={isReadOnly || readonly}
                    value={categories}
                    onChange={(e) =>
                      triggerSetCategories(e.target.value as string[])
                    }
                  >
                    {suggestedCategories.it.map((v) => (
                      <MenuItem value={v}>{v}</MenuItem>
                    ))}
                  </Select>
                </ListItem>
              )}
              {askRequiredTime && (
                <ListItem>
                  <ListItemIcon>
                    <AvTimerIcon />
                  </ListItemIcon>
                  <TextField
                    required
                    className={classes.textField}
                    type="number"
                    variant="outlined"
                    defaultValue={requiredTime}
                    onChange={(evt) =>
                      setRequiredTime(Number(evt.target.value))
                    }
                    disabled={isReadOnly || readonly}
                    label="Durata (in minuti)"
                  />
                </ListItem>
              )}
              {isComposable && (
                <ListItem>
                  <ListItemIcon>
                    <ShoppingCartIcon />
                  </ListItemIcon>
                  <TextField
                    className={classes.textField}
                    type="number"
                    variant="outlined"
                    defaultValue={minSQ}
                    onChange={(evt) => handleSetMinSQ(evt.target.value)}
                    disabled={isReadOnly || readonly}
                    label="Min qtà per ordine"
                  />
                </ListItem>
              )}
              {isComposable && (
                <ListItem>
                  <ListItemIcon>
                    <ShoppingCartIcon />
                  </ListItemIcon>
                  <TextField
                    className={classes.textField}
                    type="number"
                    variant="outlined"
                    defaultValue={maxSQ}
                    onChange={(evt) => handleSetMaxSQ(evt.target.value)}
                    disabled={isReadOnly || readonly}
                    label="Max qtà per ordine"
                  />
                </ListItem>
              )}
              <ListItem>
                <ListItemIcon>
                  <SortIcon />
                </ListItemIcon>
                <TextField
                  required
                  className={classes.textField}
                  type="number"
                  variant="outlined"
                  defaultValue={(serviceListItem.order ?? 0) + 1}
                  disabled
                  label="Ordine in lista"
                />
              </ListItem>
            </>
          </List>
        </form>
      </CardContent>
    </Card>
  );
}
